import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const MM_W = 210;
const MM_H = 297;
const JPEG_QUAL = 0.92;
const CANVAS_SCALE = 2;

const ROOT_SEL = ".pdf-print-mount .pdf-template";

function sliceCanvasToPageChunks(
  canvas: HTMLCanvasElement,
  mmW: number,
  maxMmH: number,
  qual: number,
): { dataUrl: string; mmHeight: number }[] {
  const fullMmH = (canvas.height * mmW) / canvas.width;
  if (fullMmH <= maxMmH + 0.5) {
    return [{ dataUrl: canvas.toDataURL("image/jpeg", qual), mmHeight: fullMmH }];
  }
  const pxPerMm = canvas.width / mmW;
  const slicePx = Math.max(1, Math.floor(maxMmH * pxPerMm));
  const out: { dataUrl: string; mmHeight: number }[] = [];
  let y = 0;
  while (y < canvas.height) {
    const hPx = Math.min(slicePx, canvas.height - y);
    const slice = document.createElement("canvas");
    slice.width = canvas.width;
    slice.height = hPx;
    const ctx = slice.getContext("2d");
    if (!ctx) break;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(canvas, 0, y, canvas.width, hPx, 0, 0, canvas.width, hPx);
    const mmH = (hPx * mmW) / canvas.width;
    out.push({ dataUrl: slice.toDataURL("image/jpeg", qual), mmHeight: mmH });
    y += hPx;
  }
  return out;
}

/** 导出时短暂让离屏模板可被 html2canvas 采样（部分浏览器对极左离屏画布返回空白） */
async function withPrintMountVisible<T>(fn: () => Promise<T>): Promise<T> {
  const el = document.querySelector(".pdf-print-mount") as HTMLElement | null;
  if (!el) return fn();
  const prev = el.getAttribute("style") ?? "";
  el.setAttribute(
    "style",
    [
      "position:fixed!important",
      "left:0!important",
      "top:0!important",
      "width:210mm!important",
      "max-width:210mm!important",
      "z-index:2147483646!important",
      "opacity:0.02!important",
      "pointer-events:none!important",
      "overflow:visible!important",
      "background:#fff!important",
    ].join(""),
  );
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
  try {
    return await fn();
  } finally {
    if (prev) el.setAttribute("style", prev);
    else el.removeAttribute("style");
  }
}

/**
 * 按 `.a4-page` 顺序生成 A4 PDF；单块内容超过一页高度时按 297mm 切片续页。
 */
export async function buildCompliancePdfBlob(): Promise<Blob> {
  await document.fonts.ready;
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

  return withPrintMountVisible(async () => {
    const root = document.querySelector(ROOT_SEL);
    if (!root) {
      throw new Error("PDF template root not found");
    }
    const pages = root.querySelectorAll(".a4-page");
    if (pages.length === 0) {
      throw new Error("No .a4-page sections");
    }

    const pdf = new jsPDF({
      unit: "mm",
      format: "a4",
      orientation: "portrait",
      compress: true,
    });
    let first = true;

    for (const pageEl of pages) {
      const canvas = await html2canvas(pageEl as HTMLElement, {
        scale: CANVAS_SCALE,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        windowWidth: (pageEl as HTMLElement).scrollWidth,
        windowHeight: (pageEl as HTMLElement).scrollHeight,
      });

      const chunks = sliceCanvasToPageChunks(canvas, MM_W, MM_H, JPEG_QUAL);
      for (const chunk of chunks) {
        if (!first) pdf.addPage();
        first = false;
        pdf.addImage(chunk.dataUrl, "JPEG", 0, 0, MM_W, chunk.mmHeight);
      }
    }

    return pdf.output("blob");
  });
}

export function defaultCompliancePdfFileName(scanIdShort: string): string {
  const stamp = new Date().toISOString().replace(/:/g, "-").slice(0, 19);
  const safe = scanIdShort.replace(/[^\w.-]+/g, "_").slice(0, 48);
  return `xtool-compliance-report-${safe}-${stamp}.pdf`;
}
